import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, RESORT, OFFICIAL_BOOKING_URL } from "@/lib/data";
import SectionTitle from "@/components/SectionTitle";
import RotatingVideo from "@/components/RotatingVideo";
import { EXPERIENCES_VIDEOS } from "@/lib/videos";
import { MapPin, ArrowRight } from "lucide-react";

export default function LocalArea() {
    const [exps, setExps] = useState([]);
    const [active, setActive] = useState(null);
    useEffect(() => { api.get("/experiences").then(({ data }) => setExps(data)).catch(() => {}); }, []);

    // deterministic pseudo positions across a map "canvas"
    const positions = exps.map((e, i) => ({
        left: `${12 + ((i * 17) % 76)}%`,
        top: `${18 + ((i * 23) % 60)}%`,
    }));

    return (
        <div data-testid="local-area-page">
            <RotatingVideo
                videos={EXPERIENCES_VIDEOS}
                eager
                testId="local-area-page-video"
                className="relative w-full h-[92vh] min-h-[640px]"
                overlay={
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/20 to-navy/40" />
                        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-[1px] bg-gold" /> Local Area</div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-ivory max-w-3xl leading-[1.02]">An island within reach of <span className="font-serif-italic text-gold">your terrace</span>.</h1>
                            <p className="mt-6 text-ivory/85 max-w-xl leading-relaxed">Serenity Cove sits along the coast road, minutes from Ocho Rios town and the island's most storied waterfalls.</p>
                            <div className="mt-8 flex items-center gap-3">
                                <a href="#local-map" className="btn-gold px-6 py-3 rounded-full">Explore Area</a>
                                <Link to="/booking" className="btn-outline-ivory px-6 py-3 rounded-full">Plan Your Stay</Link>
                            </div>
                        </div>
                    </>
                }
            />
            <div id="local-map" className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-16 grid md:grid-cols-5 gap-8">
                <div className="md:col-span-3 rounded-xl overflow-hidden border border-navy/10 aspect-[4/3] bg-sand/20 relative">
                    <iframe
                        title="Serenity Cove Map"
                        src={RESORT.mapEmbed}
                        className="w-full h-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
                <div className="md:col-span-2 relative rounded-xl overflow-hidden border border-navy/10 aspect-[4/3] bg-navy text-ivory">
                    <img src="https://images.pexels.com/photos/37569474/pexels-photo-37569474.jpeg" alt="Ocho Rios coastline" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-navy/50" />
                    <div className="absolute inset-0 p-6">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Interactive Map</div>
                        <div className="font-display text-2xl mt-2">Points of interest</div>
                        <div className="relative w-full h-[calc(100%-70px)] mt-4">
                            {exps.slice(0, 8).map((e, i) => (
                                <button
                                    key={e.id}
                                    data-testid={`map-marker-${e.id}`}
                                    onMouseEnter={() => setActive(e.id)}
                                    onMouseLeave={() => setActive(null)}
                                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                                    style={positions[i]}
                                >
                                    <span className="block w-3 h-3 rounded-full bg-gold ring-4 ring-gold/30 animate-marker-pulse" />
                                    {active === e.id && (
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap glass text-navy text-xs px-3 py-1.5 rounded-full">
                                            {e.name}
                                        </span>
                                    )}
                                </button>
                            ))}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                                <div className="w-4 h-4 rounded-full bg-ivory ring-4 ring-ivory/30" />
                                <div className="text-[10px] uppercase tracking-[0.24em] text-ivory/80 mt-1">Resort</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-32">
                <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-6">Nearby</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {exps.map((e) => (
                        <div key={e.id} data-testid={`nearby-${e.id}`} className="bg-white rounded-2xl p-6 border border-navy/5 hover-lift flex flex-col">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{e.category}</div>
                            <div className="font-display text-xl text-navy mt-1">{e.name}</div>
                            <div className="text-xs text-navy/60 mt-1 flex items-center gap-1.5"><MapPin size={12} /> {e.distance_km.toFixed(1)} km · {e.duration}</div>
                            <p className="text-sm text-navy/70 mt-3 leading-relaxed flex-1">{e.description}</p>
                            <Link to="/booking" data-testid={`nearby-plan-${e.id}`} className="mt-4 btn-gold px-4 py-2 rounded-full inline-flex items-center gap-1 self-start">Plan Your Stay <ArrowRight size={11} /></Link>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
